import React, { useRef } from 'react';
import { Image } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { Modal } from 'src/components/molecules/Modal/Modal';
import * as modalStyles from 'src/components/molecules/Modal/styles/ModalExternalStyles';
import { ModalLoader } from 'src/components/molecules/ModalLoader/ModalLoader';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import { ThumbnailsContainer } from 'src/components/molecules/Thumbnails/ThumbnailsContainer';
import {
  MEDIA_ID_PREFIX,
  PRODUCT_MEDIA_TABPANEL_ID_PREFIX,
} from 'src/components/organisms/ProductMediaCarousel/constants/elementIds';
import { mediaKey } from 'src/components/organisms/ProductMediaCarousel/media/buildProductMedia';
import type {
  MediaItem,
  MediaLocation,
  NextOrPreviousPayload,
  ProductMediaState,
} from 'src/components/organisms/ProductMediaCarousel/types';
import { getIconPath } from 'src/utils/assetPaths';
import * as styles from './ProductMediaModal.styles';

const ICON_CLOSE = getIconPath('icons-24-close.svg');

const ZoomAndPanWrapper = dynamic(
  async () =>
    (await import(/* webpackChunkName: "ZoomAndPanWrapper" */ './ZoomAndPanWrapper'))
      .ZoomAndPanWrapper,
  { loading: () => <ModalLoader /> },
);

export interface ProductMediaModalStateProps {
  currentMedia: ProductMediaState['currentMedia'];
  isModalOpen: ProductMediaState['isModalOpen'];
  media: MediaItem[];
}

export interface ProductMediaModalDispatchProps {
  closeModal: VoidFunction;
  onNextMedia: (payload: NextOrPreviousPayload) => void;
  onPrevMedia: (payload: NextOrPreviousPayload) => void;
}

type ProductMediaModalProps = ProductMediaModalStateProps & ProductMediaModalDispatchProps;

export const ProductMediaModal = ({
  closeModal,
  currentMedia,
  isModalOpen,
  media,
  onNextMedia,
  onPrevMedia,
}: ProductMediaModalProps) => {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const totalMedia = media.length;
  const location: MediaLocation = 'Expanded media';

  return (
    <Modal closeModal={closeModal} contentLabel="View full screen image" isModalOpen={isModalOpen}>
      <div css={[modalStyles.modalViewport, styles.modalContentContainer]} ref={innerRef}>
        <div css={styles.mediaContainer}>
          {media.map((item, i) => (
            <div
              css={styles.tabpanel}
              id={`${MEDIA_ID_PREFIX}${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`}
              key={mediaKey(item)}
              role="tabpanel"
              style={i === currentMedia ? {} : { display: 'none' }}
            >
              {item.type === 'video' ? (
                <ProductVideoSlide
                  contentPosition={i + 1}
                  isActive={currentMedia === i}
                  location="Expanded media"
                  playbackId={item.playbackId}
                  posterUrl={item.posterUrl}
                  title={item.title}
                />
              ) : (
                <ZoomAndPanWrapper visible={currentMedia === i}>
                  <Image
                    alt={`${item.title}${totalMedia > 1 ? `, ${i + 1} of ${totalMedia}` : ''}`}
                    css={styles.fullScreenMedia}
                    loading="lazy"
                    src={getProductImageURL(item.href, 'original')}
                  />
                </ZoomAndPanWrapper>
              )}
            </div>
          ))}
          {totalMedia > 1 && (
            <>
              <PreviousButton onClick={() => onPrevMedia({ location, totalMedia })} />
              <NextButton onClick={() => onNextMedia({ location, totalMedia })} />
              <ImageNumberLabel currentImage={currentMedia + 1} totalImages={totalMedia} />
            </>
          )}
        </div>
        {totalMedia > 1 && (
          <div css={styles.thumbnails}>
            <ThumbnailsContainer location={location} />
          </div>
        )}
        <button css={styles.modalCloseButton} onClick={closeModal}>
          <img alt="Close" height={24} src={ICON_CLOSE} width={24} />
        </button>
      </div>
    </Modal>
  );
};
